/**
 * §15 I6: first-party observability. Errors are always reported (a trimmed message, the first stack frame and the page
 * with ids replaced — nothing about a person). Web vitals and feature use are sent ONLY when the person turned on
 * "Help improve Room Map Pro" on the Cookies page. A 401/403 from the intake is permanent: sending stops for the rest
 * of the session (circuit breaker), so a misconfigured deployment cannot loop.
 */
import { getStorageChoice } from './storageChoice';

type Kind = 'error' | 'vital' | 'usage';
interface Ev { kind: Kind; name: string; value?: number | null; detail?: Record<string, unknown>; path?: string; build?: string }

const OFF_KEY = 'rmp2.telemetry.off';
const queue: Ev[] = [];
let broken = false;
let timer: number | undefined;

const build = () => (typeof window !== 'undefined' ? (window as unknown as { __BUILD_INFO__?: { sha?: string } }).__BUILD_INFO__?.sha : undefined) ?? 'dev';
const here = () => (typeof location !== 'undefined' ? location.pathname : '');

export function telemetryDisabled(): boolean {
  if (broken) return true;
  try { return sessionStorage.getItem(OFF_KEY) === '1'; } catch { return false; }
}
function trip() {
  broken = true;
  queue.length = 0;
  try { sessionStorage.setItem(OFF_KEY, '1'); } catch { /* the in-memory flag still holds */ }
}

/** Sends what is queued. Returns the HTTP status (0 when nothing was sent) — exported for tests. */
export async function flush(fetcher: typeof fetch = fetch): Promise<number> {
  window.clearTimeout(timer);
  if (!queue.length || telemetryDisabled()) return 0;
  const events = queue.splice(0, 20);
  try {
    const r = await fetcher('/api/telemetry', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ events }), keepalive: true });
    if (r.status === 401 || r.status === 403) trip();
    return r.status;
  } catch {
    return 0; // offline or unreachable: dropped quietly; errors are also on screen
  }
}

function enqueue(e: Ev) {
  if (telemetryDisabled()) return;
  if (e.kind !== 'error' && !getStorageChoice().analytics) return; // consent gate
  queue.push({ ...e, path: here(), build: build() });
  if (queue.length > 50) queue.splice(0, queue.length - 50);
  window.clearTimeout(timer);
  timer = window.setTimeout(() => void flush(), 5000);
}

export function reportError(err: unknown, where = 'window') {
  const e = err instanceof Error ? err : new Error(String(err));
  const frame = (e.stack ?? '').split('\n').find((l) => /at |@/.test(l))?.trim().slice(0, 200);
  enqueue({ kind: 'error', name: (e.name || 'Error').slice(0, 80), detail: { message: e.message.replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, '[email]').replace(/\+?\d[\d .()-]{7,}\d/g, '[number]').slice(0, 300), frame, where } });
}
export function track(name: string, detail: Record<string, unknown> = {}) { enqueue({ kind: 'usage', name, detail }); }
export function reportVital(name: string, value: number) { enqueue({ kind: 'vital', name, value: Math.round(value * 1000) / 1000 }); }

/** Uncaught errors, unhandled rejections, and (with consent) LCP / CLS / INP measured with PerformanceObserver. */
export function installTelemetry() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => reportError(e.error ?? e.message, 'error'));
  window.addEventListener('unhandledrejection', (e) => reportError(e.reason, 'promise'));
  let lcp = 0;
  let cls = 0;
  let inp = 0;
  const observe = (type: string, cb: (entries: PerformanceEntryList) => void) => {
    try { new PerformanceObserver((l) => cb(l.getEntries())).observe({ type, buffered: true } as PerformanceObserverInit); } catch { /* not supported */ }
  };
  observe('largest-contentful-paint', (es) => { const last = es[es.length - 1]; if (last) lcp = last.startTime; });
  observe('layout-shift', (es) => { for (const e of es as Array<PerformanceEntry & { value: number; hadRecentInput: boolean }>) if (!e.hadRecentInput) cls += e.value; });
  observe('event', (es) => { for (const e of es) inp = Math.max(inp, e.duration); });
  let sent = false;
  const sendVitals = () => {
    if (sent || !getStorageChoice().analytics) { void flush(); return; }
    sent = true;
    if (lcp) reportVital('LCP', lcp);
    reportVital('CLS', cls);
    if (inp) reportVital('INP', inp);
    void flush();
  };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') sendVitals(); });
  window.addEventListener('pagehide', sendVitals);
}
